import { useCallback, useMemo, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

type StepType =
  | "SIGNUP"
  | "PHONE_VERIFICATION"
  | "BRAND_CREATE"
  | "ENTITY_CREATE"
  | null;

const STEP_ORDER: StepType[] = [
  "SIGNUP",
  "PHONE_VERIFICATION",
  "BRAND_CREATE",
  "ENTITY_CREATE",
];

const STEP_TO_NUMBER: Record<Exclude<StepType, null>, number> = {
  SIGNUP: 1,
  PHONE_VERIFICATION: 2,
  BRAND_CREATE: 3,
  ENTITY_CREATE: 4,
};

const STEP_TO_PATH: Record<Exclude<StepType, null>, string> = {
  SIGNUP: "/signup",
  PHONE_VERIFICATION: "/phoneverify",
  BRAND_CREATE: "/profile?step=brand",
  ENTITY_CREATE: "/profile?step=entity",
};

export const ONBOARDING_STEP_IN_PROGRESS_KEY = "onboarding_step_in_progress";
export const ONBOARDING_CURRENT_STEP_KEY = "onboarding_current_step";

const getStepIndex = (step: StepType): number => {
  if (step === null) return -1;
  return STEP_ORDER.indexOf(step);
};

const canNavigateForward = (
  currentStep: StepType,
  stepInProgress: StepType
): boolean => {
  const currentIndex = getStepIndex(currentStep);
  const progressIndex = getStepIndex(stepInProgress);
  return currentIndex < progressIndex;
};

const canNavigateBack = (currentStep: StepType): boolean => {
  const currentIndex = getStepIndex(currentStep);
  return currentIndex > 0;
};

export const clearOnboardingStorage = () => {
  localStorage.removeItem(ONBOARDING_STEP_IN_PROGRESS_KEY);
  localStorage.removeItem(ONBOARDING_CURRENT_STEP_KEY);

  // Clear brand and entity data for all users
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (
      key &&
      (key.startsWith("onboarding_brand_") ||
        key.startsWith("onboarding_entities_"))
    ) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
};

export const useUserOnboardingStepper = () => {
  const history = useHistory();
  const [stepInProgress, setStepInProgressState] = useState<StepType>(null);
  const [currentStep, setCurrentStepState] = useState<StepType>(null);

  // Load from localStorage on mount
  useEffect(() => {
    const storedStepInProgress = localStorage.getItem(
      ONBOARDING_STEP_IN_PROGRESS_KEY
    ) as StepType;
    const storedCurrentStep = localStorage.getItem(
      ONBOARDING_CURRENT_STEP_KEY
    ) as StepType;

    if (storedStepInProgress) setStepInProgressState(storedStepInProgress);
    if (storedCurrentStep) setCurrentStepState(storedCurrentStep);
  }, []);

  const handleStepUpdate = (currentStep: StepType) => {
    if (currentStep && history) {
      const path = STEP_TO_PATH[currentStep];
      if (path) {
        history.replace(path);
      }
    }
  };

  const currentStepNumber = useMemo(
    () => (currentStep ? STEP_TO_NUMBER[currentStep] : -1),
    [currentStep]
  );

  const stepInProgressNumber = useMemo(
    () => (stepInProgress ? STEP_TO_NUMBER[stepInProgress] : null),
    [stepInProgress]
  );

  const canMoveBack = useMemo(
    () => canNavigateBack(currentStep),
    [currentStep]
  );

  const canMoveNext = useMemo(
    () => canNavigateForward(currentStep, stepInProgress),
    [currentStep, stepInProgress]
  );

  const saveToStorage = useCallback(
    (newStepInProgress: StepType, newCurrentStep: StepType) => {
      try {
        if (newStepInProgress) {
          localStorage.setItem(
            ONBOARDING_STEP_IN_PROGRESS_KEY,
            newStepInProgress
          );
        } else {
          localStorage.removeItem(ONBOARDING_STEP_IN_PROGRESS_KEY);
        }

        if (newCurrentStep) {
          localStorage.setItem(ONBOARDING_CURRENT_STEP_KEY, newCurrentStep);
        } else {
          localStorage.removeItem(ONBOARDING_CURRENT_STEP_KEY);
        }
      } catch {
        // Silently fail
      }
    },
    []
  );

  const initialize = useCallback(
    (step: Exclude<StepType, null>) => {
      setStepInProgressState(step);
      setCurrentStepState(step);
      saveToStorage(step, step);
    },
    [saveToStorage]
  );

  const navigateToStep = useCallback(
    (step: Exclude<StepType, null>) => {
      const targetIndex = getStepIndex(step);
      const progressIndex = getStepIndex(stepInProgress);

      // Only allow navigating to steps that have been unlocked
      if (targetIndex <= progressIndex) {
        setCurrentStepState(step);
        saveToStorage(stepInProgress, step);
      }
    },
    [stepInProgress, saveToStorage]
  );

  const goBack = useCallback(() => {
    const currentIndex = getStepIndex(currentStep);
    if (currentIndex > 0) {
      const prevStep = STEP_ORDER[currentIndex - 1];
      handleStepUpdate(prevStep);
      setCurrentStepState(prevStep);
      saveToStorage(stepInProgress, prevStep);
    }
  }, [currentStep, stepInProgress, saveToStorage]);

  const goNext = useCallback(() => {
    const currentIndex = getStepIndex(currentStep);
    const progressIndex = getStepIndex(stepInProgress);

    // Calculate next step
    const nextStep = STEP_ORDER[currentIndex + 1];

    if (nextStep) {
      const nextStepIndex = getStepIndex(nextStep);

      // If next step is beyond current progress, advance the progress
      if (nextStepIndex > progressIndex) {
        setStepInProgressState(nextStep);
        setCurrentStepState(nextStep);
        saveToStorage(nextStep, nextStep);
      } else {
        // Next step is already unlocked, just navigate
        setCurrentStepState(nextStep);
        saveToStorage(stepInProgress, nextStep);
      }

      handleStepUpdate(nextStep);
    }
  }, [currentStep, stepInProgress, saveToStorage]);

  const advanceProgress = useCallback(
    (step: Exclude<StepType, null>) => {
      const targetIndex = getStepIndex(step);
      const progressIndex = getStepIndex(stepInProgress);

      // Only advance if the new step is ahead of current progress
      if (targetIndex > progressIndex) {
        setStepInProgressState(step);
        setCurrentStepState(step);
        saveToStorage(step, step);
      }
    },
    [stepInProgress, saveToStorage]
  );

  const reset = useCallback(() => {
    setStepInProgressState(null);
    setCurrentStepState(null);
    clearOnboardingStorage();
  }, []);

  return {
    currentStep,
    stepInProgress,
    currentStepNumber,
    stepInProgressNumber,
    canMoveBack,
    canMoveNext,
    initialize,
    advanceProgress,
    navigateToStep,
    goBack,
    goNext,
    reset,
  };
};
